import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { style } from 'typestyle';
import * as styles from '../statistics-tabs.styles';
import * as localStyles from './default-thresholds-tab.styles';
import DashboardTheme from '../dashboard-theme';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../../core/components/buttons/primary-button.component';
import notify from '../../../../core/components/notify/notify.component';
import StyleConstants from '../../../../core/styles.constants';
import RouteConstants from '../../../../core/routes.constants';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { useKeycloak } from '../../../authentication/keycloak/KeycloakProvider';
import { useDefaultThreshold } from '../hooks/dashboard.hook';
import { updateDefaultThreshold } from '../services/dashboard.service';
import Gauge from '../components/gauge.component';
import { useCompanies } from '../../../companies/hooks/companies.hook';
import AppConstants from '../../../../core/app.constants';

const DEFAULT_MIN = 60;
const DEFAULT_MAX = 80;
const PREVIEW_VALUE = 71;

interface Props {
  readonly onSaved?: () => void;
}

const DefaultThresholdsTab = ({ onSaved }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const keycloak = useKeycloak();
  const isMobile = useContext(IsMobileContext);
  const isWhyellow = keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE);
  const { companies } = useCompanies();

  // Without an explicit company, this tab (and the backend behind it) silently falls back to
  // "whichever company the caller's first administered office happens to belong to" — for a
  // whyellow user who oversees multiple companies, that meant saving here only ever updated one
  // company, with no indication of which, leaving every other company's offices stuck on the
  // seeded 60/80 default forever. Let a whyellow user pick which company they're editing.
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);

  const { initialized, threshold, error, refresh } = useDefaultThreshold(isWhyellow ? selectedCompanyId : undefined);

  const [minPercentage, setMinPercentage] = useState<number>(DEFAULT_MIN);
  const [maxPercentage, setMaxPercentage] = useState<number>(DEFAULT_MAX);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (threshold) {
      setMinPercentage(threshold.minPercentage);
      setMaxPercentage(threshold.maxPercentage);
    }
  }, [threshold]);

  const isValid = minPercentage >= 0 && maxPercentage <= 100 && minPercentage < maxPercentage;

  const saveThreshold = useCallback(async (): Promise<void> => {
    if (!isValid) {
      return;
    }

    setSaving(true);
    const result = await updateDefaultThreshold(minPercentage, maxPercentage, keycloak, isWhyellow ? selectedCompanyId : undefined);
    setSaving(false);

    if (result) {
      notify(t('dashboard-thresholds-save-success-message'), true);
      refresh();
      onSaved?.();
    } else {
      notify(t('dashboard-thresholds-save-error-message'), false);
    }
  }, [isValid, minPercentage, maxPercentage, keycloak, refresh, onSaved, isWhyellow, selectedCompanyId, t]);

  const goToOffices = useCallback((): void => {
    navigate(`/${RouteConstants.PATH_OFFICES}`);
  }, [navigate]);

  if (!initialized) {
    return (
      <Center>
        <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
      </Center>
    );
  }

  if (error) {
    return <span className={style(styles.errorMessage)}>{t('dashboard-thresholds-error-message')}</span>;
  }

  return (
    <div>
      <h2 className={style(styles.panelLead)}>{t('dashboard-tab-thresholds')}</h2>
      <p className={style(styles.pageSub)}>{t('dashboard-thresholds-page-description')}</p>

      {isWhyellow && (
        <div className={style(localStyles.companySelectRow)}>
          <span className={style(localStyles.companySelectLabel)}>{t('dashboard-thresholds-company-label')}</span>
          <select
            className={style(localStyles.companySelect)}
            value={selectedCompanyId ?? ''}
            onChange={(event): void => setSelectedCompanyId(event.target.value || undefined)}
          >
            <option value="">{t('dashboard-thresholds-company-placeholder')}</option>
            {(companies ?? []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={style(localStyles.grid(isMobile))}>
        <div className={style(styles.card)}>
          <div className={style(styles.cardTitle)}>{t('dashboard-thresholds-org-default-title')}</div>
          <div className={style(styles.rule)} />

          <div className={style(localStyles.thresholdRow)}>
            <div className={style(styles.gaugeWrap)}>
              <Gauge value={PREVIEW_VALUE} thresholdMin={minPercentage} thresholdMax={maxPercentage} size={150} />
            </div>
            <div className={style(localStyles.thresholdInputs)}>
              <div className={style(localStyles.inputLabel)}>{t('dashboard-thresholds-min-label')}</div>
              <input
                type="range"
                min={0}
                max={100}
                value={minPercentage}
                onChange={(event): void => setMinPercentage(Number(event.target.value))}
                style={{ width: '100%', accentColor: DashboardTheme.navy800 }}
              />
              <div className={style(localStyles.sliderLabels)}>
                <span>0%</span>
                <span className={style(localStyles.sliderValue)}>{minPercentage}%</span>
                <span>100%</span>
              </div>

              <div className={style(localStyles.inputLabelSpaced)}>{t('dashboard-thresholds-max-label')}</div>
              <input
                type="range"
                min={0}
                max={100}
                value={maxPercentage}
                onChange={(event): void => setMaxPercentage(Number(event.target.value))}
                style={{ width: '100%', accentColor: DashboardTheme.navy800 }}
              />
              <div className={style(localStyles.sliderLabels)}>
                <span>0%</span>
                <span className={style(localStyles.sliderValue)}>{maxPercentage}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {!isValid && <div className={style(localStyles.validationMessage)}>{t('dashboard-thresholds-validation-message')}</div>}

          <div className={style(localStyles.saveRow(isMobile))}>
            <div className={style(localStyles.saveButtonWrap)}>
              <PrimaryButton disabled={!isValid || saving} loading={saving} onClick={saveThreshold}>
                {t('dashboard-thresholds-save-button')}
              </PrimaryButton>
            </div>
            <span className={style(styles.pageSub, { margin: 0, flex: 1, minWidth: 0 })}>
              {t('dashboard-thresholds-applies-note')}
            </span>
          </div>
        </div>

        <div className={style(styles.card)}>
          <div className={style(styles.cardTitle)}>{t('dashboard-thresholds-overrides-title')}</div>
          <div className={style(styles.rule)} />
          <p className={style(localStyles.description)}>{t('dashboard-thresholds-explanation-message')}</p>

          <div className={style(localStyles.previewBox)}>
            <div className={style(localStyles.previewBoxLabel)}>{t('dashboard-thresholds-preview-label')}</div>
            <div className={style(localStyles.previewRow)}>
              <div className={style(localStyles.previewRowName)}>
                <div className={style(localStyles.previewRoomName)}>{t('dashboard-thresholds-preview-room-name')}</div>
                <div className={style(localStyles.previewRoomCapacity)}>{t('dashboard-thresholds-preview-room-capacity')}</div>
              </div>
              <div className={style(localStyles.previewBadgeWrap)}>
                <div className={style(localStyles.previewBadgeLabel)}>{t('dashboard-thresholds-min-label')}</div>
                <div className={style(localStyles.previewBadgeValue)}>50%</div>
              </div>
              <div className={style(localStyles.previewBadgeWrap)}>
                <div className={style(localStyles.previewBadgeLabel)}>{t('dashboard-thresholds-max-label')}</div>
                <div className={style(localStyles.previewBadgeValue)}>85%</div>
              </div>
            </div>
          </div>

          <div className={style(styles.callout, { marginTop: 16 })}>
            <div>{t('dashboard-thresholds-callout-message')}</div>
          </div>

          <div className={style(localStyles.saveButtonWrap, { marginTop: 16 })}>
            <PrimaryButton onClick={goToOffices}>{t('dashboard-thresholds-go-to-offices-button')}</PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultThresholdsTab;
